import React from "react";

import { Link } from "react-router-dom";

import { BreadcrumbsWithMatterName } from "./Breadcrumbs";

import ButtonLink from "./ButtonLink";

import {
	iconMap,
	mediumDateFormat,
	pluralize,
	pluralizeWithNum,
	getDocumentLockText,
	mediumDateFormatMinusTimezone,
	numberWithCommas,
} from "../utilities/utilities";

import "./SearchResults.css";
import {
	getDisabledStateFromLifecycleStatuses,
	TRASH_LIFECYCLE_STATUS,
	ARCHIVE_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";
import {
	IconEye,
	IconLogout,
	IconInfoCircle,
	IconStar,
	IconTrash,
	IconArchive,
	IconUserEdit,
	IconUsersGroup,
	IconLayoutNavbarCollapse,
	IconLayoutBottombarCollapse,
} from "@tabler/icons-react";
import { ActionIcon, Divider } from "@mantine/core";

import DocumentSlidingPane from "./DocumentPreviewRightPane";
import useDocumentPreviewPane from "./useDocumentPreviewPane";

const SearchResultPreviewButton = ({ title, onClick }) => (
	<ActionIcon
		variant="subtle"
		size="md"
		className="searchResultPreviewButton"
		title={title}
		style={{ marginLeft: 8 }}
		onClick={onClick}
	>
		<IconEye size={18} />
	</ActionIcon>
);

const SearchResultsDivider = ({ stretchIntoHorizontalPadding = 0 }) => (
	<Divider
		style={{
			marginTop: 4,
			marginBottom: 4,
			marginLeft: -stretchIntoHorizontalPadding,
			marginRight: -stretchIntoHorizontalPadding,
		}}
	/>
);

const TagPill = (props) => {
	return props.text ? (
		<span title={props.text} className="searchTagPill">
			{props.text}
		</span>
	) : null;
};
let maxShownTags = 3;
const LimitShownTags = ({ tagIDs, documentID, documentTagNames }) => {
	if (!tagIDs.length) {
		return null;
	}
	let tagNames = tagIDs.map((tagID) => documentTagNames[tagID]);
	let shownTags = tagNames.slice(0, maxShownTags);
	let hiddenTags = tagNames.slice(maxShownTags);
	let hiddenTagsText = hiddenTags.join("\n");
	return (
		<div className="searchTagTypeWrapper">
			<span className="MainFontBold">Tags:</span>
			{shownTags.map((tagName, i) => (
				<TagPill
					tagIndex={i}
					key={"tag-" + documentID + "-" + tagIDs[i]}
					text={tagName}
				/>
			))}
			{hiddenTagsText && (
				<span className="searchTagPill" title={hiddenTagsText}>
					{`and ${hiddenTags.length} more `}
					<IconInfoCircle
						size={18}
						style={{ verticalAlign: "text-bottom" }}
					/>
				</span>
			)}
		</div>
	);
};
const DocumentTagsTypes = (props) => {
	const typeID = props.documentTypeID;
	const statusID = props.documentStatusID;
	const tagIDs = props.documentTagIDs;
	return (
		<div style={{ display: "flex", flexDirection: "column" }}>
			{typeID ? (
				<div className="searchTagTypeWrapper">
					<span className="MainFontBold">Type:</span>
					<TagPill text={props.documentTypeNames[typeID]} />
				</div>
			) : null}
			{statusID ? (
				<div className="searchTagTypeWrapper">
					<span className="MainFontBold">Status:</span>
					<TagPill text={props.documentStatusNames[statusID]} />
				</div>
			) : null}
			<LimitShownTags
				tagIDs={tagIDs}
				documentID={props.documentID}
				documentTagNames={props.documentTagNames}
			/>
		</div>
	);
};
const DocumentLockStatus = (props) => {
	/*
	lockStatus:
		0 - 
		1 - Currently in use by Steve
		2 - Currently checked out to Steve
		3 - Currently checked out and in use by Steve
	*/
	return props.lockStatus ? (
		<div style={{ display: "flex", gap: 8 }}>
			<div>
				{getDocumentLockText(
					props.lockStatus,
					props.userNames[props.lockedBy]
				)}
			</div>
			{
				//checked out
				props.lockStatus > 1 && (
					<IconLogout
						color={"var(--lxw-main-500)"}
						size={18}
						style={{ marginTop: 2 }}
					/>
				)
			}
			{
				// in use
				props.lockStatus % 2 === 1 && (
					<IconUserEdit
						color={"var(--lxw-main-500)"}
						size={18}
						style={{ marginTop: 2 }}
					/>
				)
			}
		</div>
	) : null;
};
const DocumentResult = (props) => {
	const { iconComponent } = iconMap({
		original: { filename: props.filename, type: "File" },
	});

	// only ever show one comment snippet: a major version comment match wins,
	// otherwise fall back to a versionComment match
	const versionCommentSnippet =
		props.innerHits.majorVersionHighlights &&
		props.innerHits.majorVersionHighlights[0] &&
		props.innerHits.majorVersionHighlights[0].major_version_comment
			? props.innerHits.majorVersionHighlights[0].major_version_comment
			: props.innerHits.highlights &&
				  props.innerHits.highlights[0] &&
				  props.innerHits.highlights[0].versionComment
				? props.innerHits.highlights[0].versionComment
				: null;

	return (
		<div style={{ marginTop: 36 }}>
			<div style={{ fontSize: 18 }}>
				{/* document name, favorite and in trash */}
				<div style={{ display: "flex", alignItems: "center" }}>
					{iconComponent}
					<Link to={`/docs/${props.id}`}>
						{props.documentHighlight &&
						props.documentHighlight.documentNameSearchable ? (
							<span
								dangerouslySetInnerHTML={{
									__html: props.documentHighlight
										.documentNameSearchable[0],
								}}
							/>
						) : (
							props.filename
						)}
					</Link>
					{/*<div>({props.score})</div>*/}
					{props.isFavorite && (
						<IconStar
							size={16}
							fill={"var(--lw-gold)"}
							style={{ marginLeft: 8 }}
						/>
					)}
					{props.source.documentLifecycleStatus ===
						TRASH_LIFECYCLE_STATUS && (
						<IconTrash size={16} style={{ marginLeft: 8 }} />
					)}
					{props.source.documentLifecycleStatus ===
						ARCHIVE_LIFECYCLE_STATUS && (
						<IconArchive size={16} style={{ marginLeft: 8 }} />
					)}
					<SearchResultPreviewButton
						title="Preview This Document"
						onClick={() =>
							props.onPreview({
								type: "File",
								id: props.id,
								title: props.filename,
								rowData: {
									isFavorite: !!props.isFavorite,
									versionNumber: props.currentVersion,
									documentLifecycleStatus:
										props.source.documentLifecycleStatus,
									matterOrFolderLifecycleStatusObject:
										getDisabledStateFromLifecycleStatuses({
											matterLifecycleStatus:
												props.matterLifecycleStatus,
										}),
								},
							})
						}
					/>
				</div>
			</div>
			{/* Divider */}
			<SearchResultsDivider />
			{/* document ID */}
			<div>
				<span className="MainFontBold">Document ID:</span>
				<span> {props.id}</span>
			</div>
			<DocumentTagsTypes {...props} />
			<DocumentLockStatus {...props} />
			<div>
				<span>
					{props.innerHits.count ? (
						<span>
							Results in{" "}
							{numberWithCommas(props.innerHits.count) +
								pluralize(" version", props.innerHits.count)}
							.{" "}
						</span>
					) : null}
					<span>
						Last modified:{" "}
						{mediumDateFormat(
							props.source.modifiedDate
								? parseInt(props.source.modifiedDate, 10)
								: props.lastModified
						)}{" "}
						{props.userNames && props.documentModifiedBy
							? "by " + props.userNames[props.documentModifiedBy]
							: ""}
					</span>
				</span>
			</div>
			<SearchResultsDivider />

			<ClientNameMatterNameAndBreadcrumbs {...props} />
			<SearchResultsDivider />

			{props.innerHits.count &&
			props.innerHits.highlights &&
			props.innerHits.highlights[0] &&
			props.innerHits.highlights[0].content ? (
				<div style={{ wordBreak: "break-all" }}>
					<div
						key={props.id + "_content"}
						dangerouslySetInnerHTML={{
							__html: props.innerHits.highlights[0].content,
						}}
					/>{" "}
					<div style={{ marginTop: 4 }}>(found in document body)</div>
				</div>
			) : null}
			{versionCommentSnippet ? (
				<div style={{ wordBreak: "break-all" }}>
					<div
						key={props.id + "_version_comment"}
						dangerouslySetInnerHTML={{
							__html: versionCommentSnippet,
						}}
					/>{" "}
					<div style={{ marginTop: 4 }}>
						(found in document version comment)
					</div>
				</div>
			) : null}
			{props.documentHighlight && props.documentHighlight.documentNote ? (
				<div style={{ wordBreak: "break-all" }}>
					{props.documentHighlight.documentNote.map(
						(snippet, dex) => (
							<div
								key={props.id + "_" + dex}
								dangerouslySetInnerHTML={{ __html: snippet }}
							/>
						)
					)}

					<div style={{ marginTop: 4 }}>
						(found in document internal note)
					</div>
				</div>
			) : null}
		</div>
	);
};
const MatterNoteResult = (props) => {
	const { iconComponent } = iconMap({
		original: { type: "Note" },
	});
	return (
		<div style={{ marginTop: 36 }}>
			<div style={{ fontSize: 18 }}>
				<div style={{ display: "flex", alignItems: "center" }}>
					{iconComponent}
					<Link to={`/notes/${props.source.matterNoteID}`}>
						{props.highlight &&
						props.highlight.matterNoteNameSearchable ? (
							<span
								dangerouslySetInnerHTML={{
									__html: props.highlight
										.matterNoteNameSearchable[0],
								}}
							/>
						) : (
							props.source.matterNoteName
						)}
					</Link>
					{props.source.matterNoteLifecycleStatus ===
						TRASH_LIFECYCLE_STATUS && (
						<IconTrash size={16} style={{ marginLeft: 8 }} />
					)}
					{props.source.matterNoteLifecycleStatus ===
						ARCHIVE_LIFECYCLE_STATUS && (
						<IconArchive size={16} style={{ marginLeft: 8 }} />
					)}
					<SearchResultPreviewButton
						title="Preview This Note"
						onClick={() =>
							props.onPreview({
								type: "Note",
								id: props.source.matterNoteID,
								title: props.source.matterNoteName,
								rowData: {
									matterNoteLifecycleStatus:
										props.source.matterNoteLifecycleStatus,
									matterOrFolderLifecycleStatusObject:
										getDisabledStateFromLifecycleStatuses({
											matterLifecycleStatus:
												props.matterLifecycleStatus,
										}),
								},
							})
						}
					/>
				</div>
			</div>
			<SearchResultsDivider />
			<div>
				{props.modifiedOn
					? "Last modified: " +
						mediumDateFormatMinusTimezone(props.modifiedOn)
					: ""}
				{props.userNames && props.modifiedBy
					? " by " + props.userNames[props.modifiedBy]
					: ""}
			</div>
			<SearchResultsDivider />
			<ClientNameMatterNameAndBreadcrumbs
				{...props}
				rootName="Notes"
				rootPath="/notes/"
			/>
			<SearchResultsDivider />
			<div>
				{props.highlight && props.highlight.content ? (
					<div style={{ wordBreak: "break-all" }}>
						{props.highlight.content.map((snippet, dex) => (
							<div
								key={props.id + "_" + dex}
								dangerouslySetInnerHTML={{ __html: snippet }}
							/>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
};
const EmailResult = (props) => {
	const { iconComponent } = iconMap({
		original: { type: "Email" },
	});
	return (
		<div style={{ marginTop: 36 }}>
			<div style={{ fontSize: 18 }}>
				<div style={{ display: "flex", alignItems: "center" }}>
					{iconComponent}
					<ButtonLink
						href=""
						onClick={(e) => {
							const prefix = `${props.source.emailID}_${props.source.originalFilename}`;
							props.emailClicked(prefix);
							e.preventDefault();
							return false;
						}}
					>
						{props.highlight && props.highlight.emailSubject ? (
							<div style={{ wordBreak: "break-all" }}>
								<span
									dangerouslySetInnerHTML={{
										__html: props.highlight.emailSubject[0],
									}}
								/>
							</div>
						) : (
							props.subjectline
						)}
					</ButtonLink>
					{/*<div>({props.score})</div>*/}
					<SearchResultPreviewButton
						title="Preview This Email"
						onClick={() =>
							props.onPreview({
								type: "Email",
								id: props.id,
								title: props.subjectline,
								rowData: {
									originalFilename:
										props.source.originalFilename,
									emailFrom: props.emailFrom,
									emailTo: props.emailTo,
									date: props.date,
									emailLifecycleStatus:
										props.source.emailLifecycleStatus,
									matterOrFolderLifecycleStatusObject:
										getDisabledStateFromLifecycleStatuses({
											matterLifecycleStatus:
												props.matterLifecycleStatus,
										}),
								},
							})
						}
					/>
				</div>
			</div>
			<SearchResultsDivider />
			<div>
				<span className="MainFontBold">Date:</span>
				<span> {mediumDateFormat(props.date)}</span>
			</div>
			<div>
				<span className="MainFontBold">From:</span>
				<span>
					{" "}
					{props.highlight && props.highlight.emailFromSearchable ? (
						<span
							dangerouslySetInnerHTML={{
								__html: props.highlight.emailFromSearchable[0],
							}}
						/>
					) : (
						props.emailFrom
					)}
				</span>
			</div>
			<div>
				<span className="MainFontBold">To:</span>
				<span>
					{" "}
					{props.highlight && props.highlight.emailToSearchable ? (
						<span
							dangerouslySetInnerHTML={{
								__html: props.highlight.emailToSearchable[0],
							}}
						/>
					) : (
						props.emailTo
					)}
				</span>
			</div>
			<SearchResultsDivider />
			<ClientNameMatterNameAndBreadcrumbs
				{...props}
				rootName="Email"
				rootPath="/email/"
			/>

			<SearchResultsDivider />
			<div>
				{props.highlight && props.highlight.content ? (
					<div style={{ wordBreak: "break-all" }}>
						{props.highlight.content.map((snippet, dex) => (
							<div
								key={props.id + "_" + dex}
								dangerouslySetInnerHTML={{ __html: snippet }}
							/>
						))}
						<div style={{ marginTop: 4 }}>
							(found in email body)
						</div>
					</div>
				) : null}
				{props.highlight && props.highlight.emailNote ? (
					<div style={{ wordBreak: "break-all" }}>
						{props.highlight.emailNote.map((snippet, dex) => (
							<div
								key={props.id + "_" + dex}
								dangerouslySetInnerHTML={{ __html: snippet }}
							/>
						))}
						<div style={{ marginTop: 4 }}>
							(found in email short note)
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};
const addUpUniqueRecordsFromBucket = (buckets) => {
	return buckets.reduce((acc, cur) => {
		if (
			cur.key === "docs" ||
			cur.key === "email" ||
			cur.key === "matterNote"
		) {
			return acc + cur.doc_count;
		} else {
			return acc;
		}
	}, 0);
};

const displayFn = {
	byMatterID: (aggs, names) => {
		const singleMatterRecordCount = (matterResult) => (
			<tr key={matterResult.matterID}>
				<td
					style={{
						marginRight: 16,
						overflow: "hidden",
						textOverflow: "ellipsis",
						maxWidth: 750,
					}}
					title={matterResult.matterName}
				>
					{/*<a
						href=""
						onClick={(e) => {
							console.log(matterResult.matterID);
							onMatterNameClick(matterResult.matterID);
							e.preventDefault();
							return false;
						}}
					>*/}
					{matterResult.matterName}
					{/*</a>*/}
				</td>
				<td>{numberWithCommas(matterResult.recordCount)}</td>
			</tr>
		);
		const numTopMattersToDisplay = 5;
		if (names) {
			const topMatters = aggs.buckets
				.filter((val) => names[val.key])
				.map((val) => {
					return {
						matterID: val.key,
						matterName: names[val.key],
						recordCount: addUpUniqueRecordsFromBucket(
							val.byRecordType.buckets
						),
					};
				})
				.sort((a, b) => {
					return b.recordCount - a.recordCount;
				})
				.slice(0, numTopMattersToDisplay);

			return (
				<div style={{ display: "flex" }}>
					<div>
						<table>
							<tbody>
								{topMatters
									.slice(0, numTopMattersToDisplay)
									.map(singleMatterRecordCount)}
							</tbody>
						</table>
					</div>
				</div>
			);
		} else {
			return null;
		}
	},
	/*"byRecordType": aggs => (
									<div key="by-recordType">
										{aggs.buckets
											.map(x =>
												pluralizeWithNum(
													recordTypeMap[x.key]
														.display,
													x.doc_count
												)
											)
											.join(", ")}
									</div>
								),*/
	/*uniqueDocumentIDCount: aggs => (
									<div>
										{pluralizeWithNum(
											"Document",
											aggs.value
										)}
									</div>
								),*/
	byDocumentID: () => null,
};

const AggregationResults = (props) => {
	if (props.tabAggregations) {
		const { tabAggregations, queryTook, query, totalHits, matterNames } =
			props;
		const { uniqueDocumentIDCount, byRecordType, byMatterID } =
			tabAggregations;

		var resultCountArray = [];

		if (uniqueDocumentIDCount && uniqueDocumentIDCount.value > 0) {
			resultCountArray.push(
				pluralizeWithNum(
					"Document",
					numberWithCommas(uniqueDocumentIDCount.value)
				)
			);
		}
		if (byRecordType && byRecordType.buckets) {
			byRecordType.buckets.forEach((x) => {
				if (x.doc_count > 0 && x.key !== "docs") {
					resultCountArray.push(
						props.recordTypeMap[x.key]
							? pluralizeWithNum(
									props.recordTypeMap[x.key].display,
									numberWithCommas(x.doc_count)
								)
							: ""
					);
				}
			});
		}

		return uniqueDocumentIDCount && byRecordType ? (
			<div>
				<div style={{ marginBottom: 16, marginTop: 16 }}>
					{totalHits ? (
						<span>Search completed in {queryTook} seconds</span>
					) : (
						<span>
							We didn&apos;t find any items containing &quot;
							{query}
							&quot;. Please reconsider your search criteria and
							try again.
						</span>
					)}
				</div>
				{totalHits ? (
					<div style={{ display: "flex" }}>
						<div
							style={{
								padding: 16,
								backgroundColor: "var(--lw-light-grey-a)",
								width: "100%",
								maxWidth: 800,
							}}
						>
							<div>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									<span
										style={{
											color: "black",
										}}
										className="MainFontBold"
									>
										Top Matters
									</span>
									<div
										onClick={() =>
											props.toggleTopMattersSearchResults()
										}
										style={{
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
										}}
									>
										{props.topMattersSearchResultsExpanded ? (
											<IconLayoutNavbarCollapse
												size={20}
											/>
										) : (
											<IconLayoutBottombarCollapse
												size={20}
											/>
										)}
									</div>
								</div>
								<SearchResultsDivider
									stretchIntoHorizontalPadding={16}
								/>
							</div>
							{props.topMattersSearchResultsExpanded && (
								<div>
									<div style={{ marginBottom: 16 }}>
										(Matters with the most{" "}
										{props.activeTabName === "All"
											? "items"
											: props.activeTabName}{" "}
										matching this search)
									</div>
									{byMatterID &&
										displayFn.byMatterID(
											byMatterID,
											matterNames,
											props.matterNameClickAction
										)}
								</div>
							)}
						</div>
					</div>
				) : null}
			</div>
		) : (
			<div style={{ width: 600, fontSize: 14 }}>
				<div
					style={{
						fontWeight: "bold",
						marginBottom: 16,
						marginTop: 16,
					}}
				>
					Enter search criteria above to get started.
				</div>
				LexWorkplace includes full-text search across all of your
				firm&apos;s documents. Optionally refine your search by matter,
				document type, author and more.
			</div>
		);
	} else {
		return <div> no aggs</div>;
	}
};
const ClientNameMatterNameAndBreadcrumbs = (props) => {
	return (
		<div>
			{props.clientName && (
				<div style={{ marginBottom: 4 }}>
					<IconUsersGroup
						size={16}
						style={{ verticalAlign: "text-bottom", marginRight: 4 }}
					/>
					Client:{" "}
					<Link to={`/clients/${props.clientID}`}>
						{props.clientName}
					</Link>
				</div>
			)}
			<BreadcrumbsWithMatterName
				{...props}
				matterNameAsLink={true}
				showMatterIconWithMatterName={true}
			/>
		</div>
	);
};
const SearchResults = (props) => {
	let data = props.docs.concat(props.emails).concat(props.matterNotes);

	const {
		documentTypeNames,
		documentStatusNames,
		documentTagNames,
		userNames,
	} = props;

	const { previewOpen, previewSelectedRow, openItemPreview, closePreview } =
		useDocumentPreviewPane();

	const componentMap = {
		docs: (pp) => (
			<DocumentResult
				{...pp}
				userNames={userNames}
				documentTypeNames={documentTypeNames}
				documentStatusNames={documentStatusNames}
				documentTagNames={documentTagNames}
				onPreview={openItemPreview}
			/>
		),
		documentVersion: (pp) => (
			<DocumentResult
				{...pp}
				userNames={userNames}
				documentTypeNames={documentTypeNames}
				documentStatusNames={documentStatusNames}
				documentTagNames={documentTagNames}
				onPreview={openItemPreview}
			/>
		),
		email: (pp) => (
			<EmailResult
				{...pp}
				emailClicked={props.emailClicked}
				onPreview={openItemPreview}
			/>
		),
		matterNote: (pp) => (
			<MatterNoteResult
				{...pp}
				matterNoteClicked={props.matterNoteClicked}
				userNames={userNames}
				onPreview={openItemPreview}
			/>
		),
	};
	return data ? (
		data.length ? (
			<div>
				<div>
					{data
						.sort((a, b) => b.score - a.score)
						.map((d, index) => {
							return (
								<div
									key={`result_${index}`}
									className="searchResult"
								>
									{componentMap[d.source.recordType](d)}
								</div>
							);
						})}
				</div>
				{previewSelectedRow ? (
					<DocumentSlidingPane
						openPane={previewOpen}
						selectedRowData={previewSelectedRow}
						closeRightPane={closePreview}
					/>
				) : null}
			</div>
		) : null
	) : null;
};
export { AggregationResults, SearchResults };
